/**
 * Tema aplicatiei: luminoasa, intunecata sau ca sistemul (12.10.2026).
 *
 * Alegerea se tine pe calculatorul fiecaruia (`localStorage`). Tema se pune ca
 * `data-theme="dark"` pe <html> — valorile paletei se schimba in `styles/tema.css`, iar
 * `dark:` din Tailwind raspunde la acelasi atribut. Un script mic din `index.html` o pune
 * inainte sa porneasca React, ca pagina sa nu clipeasca alba la incarcare.
 */
import { useSyncExternalStore } from 'react'

export type Tema = 'luminos' | 'intunecat' | 'sistem'

const CHEIE = 'b4l.tema'

export function temaAleasa(): Tema {
  try {
    const t = localStorage.getItem(CHEIE)
    if (t === 'luminos' || t === 'intunecat' || t === 'sistem') return t
  } catch {
    // stocare blocata (fereastra privata): ramane tema luminoasa
  }
  return 'luminos'
}

const sistemIntunecat = () => window.matchMedia?.('(prefers-color-scheme: dark)').matches ?? false

/** Pune tema pe pagina. */
export function aplicaTema(t: Tema = temaAleasa()): void {
  const intunecat = t === 'intunecat' || (t === 'sistem' && sistemIntunecat())
  if (intunecat) document.documentElement.setAttribute('data-theme', 'dark')
  else document.documentElement.removeAttribute('data-theme')
}

/** Salveaza si aplica alegerea. Pe „sistem", urmareste si schimbarile din sistem. */
export function alegeTema(t: Tema): void {
  try {
    localStorage.setItem(CHEIE, t)
  } catch {
    // fara stocare, alegerea tine pana la reincarcare
  }
  aplicaTema(t)
}

let urmarit = false
/** Cand tema e „sistem", schimbarea din sistem (zi/noapte) se vede imediat. */
export function urmaresteSistemul(): void {
  if (urmarit || !window.matchMedia) return
  urmarit = true
  window.matchMedia('(prefers-color-scheme: dark)').addEventListener('change', () => {
    if (temaAleasa() === 'sistem') aplicaTema('sistem')
  })
}

/**
 * Daca pagina e acum pe tema intunecata, urmarit pe loc. Pentru piesele din afara paletei, care
 * isi au culorile lor — notificarile (`sonner`) au fundal propriu si raman albe fara asta.
 */
export function useTemaIntunecata(): boolean {
  return useSyncExternalStore(
    (anunta) => {
      const o = new MutationObserver(anunta)
      o.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] })
      return () => o.disconnect()
    },
    () => document.documentElement.getAttribute('data-theme') === 'dark',
  )
}
